/**
 * GuardianDashboard
 *
 * Dashboard for managing guardian enrollments.
 * Shows guardians for the current user and provides actions.
 */

import React from 'react';
import {
  Container,
  Title,
  Text,
  Paper,
  Group,
  Button,
  Stack,
  Badge,
  Table,
  ActionIcon,
  Menu,
  Loader,
  Alert,
  Tabs,
} from '@mantine/core';
import {
  IconPlus,
  IconDotsVertical,
  IconEye,
  IconBan,
  IconTrash,
  IconAlertCircle,
  IconUsers,
  IconUserCheck,
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import {
  useGuardian,
  Guardian,
  getGuardianTypeLabel,
  getGuardianStatusLabel,
  getGuardianStatusColor,
} from './hooks/useGuardian';

export function GuardianDashboard() {
  const navigate = useNavigate();
  const {
    myGuardians,
    guardedUsers,
    loading,
    error,
    revokeGuardian,
    suspendGuardian,
    clearError,
  } = useGuardian();

  const handleAddGuardian = () => {
    navigate('/guardian/enroll');
  };

  const handleViewGuardian = (guardian: Guardian) => {
    navigate(`/guardian/${guardian.guardianId}`);
  };

  const handleSuspendGuardian = async (guardian: Guardian) => {
    if (window.confirm('Are you sure you want to suspend this guardian?')) {
      await suspendGuardian(guardian.guardianId, guardian.userId, 'Suspended by user');
    }
  };

  const handleRevokeGuardian = async (guardian: Guardian) => {
    if (window.confirm('Are you sure you want to permanently revoke this guardian? This cannot be undone.')) {
      await revokeGuardian(guardian.guardianId, guardian.userId);
    }
  };

  const formatDate = (isoString?: string) => {
    if (!isoString) return '-';
    return new Date(isoString).toLocaleDateString();
  };

  const renderGuardianTable = (guardians: Guardian[], showActions = true) => {
    if (guardians.length === 0) {
      return (
        <Paper p="xl" ta="center" bg="gray.0">
          <Text c="dimmed">No guardians found</Text>
        </Paper>
      );
    }

    return (
      <Table striped highlightOnHover>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Name</Table.Th>
            <Table.Th>Type</Table.Th>
            <Table.Th>Status</Table.Th>
            <Table.Th>Relationship</Table.Th>
            <Table.Th>Created</Table.Th>
            {showActions && <Table.Th>Actions</Table.Th>}
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {guardians.map((guardian) => (
            <Table.Tr key={guardian.guardianId}>
              <Table.Td>{guardian.guardianName}</Table.Td>
              <Table.Td>{getGuardianTypeLabel(guardian.guardianType)}</Table.Td>
              <Table.Td>
                <Badge color={getGuardianStatusColor(guardian.status)}>
                  {getGuardianStatusLabel(guardian.status)}
                </Badge>
              </Table.Td>
              <Table.Td>{guardian.relationship}</Table.Td>
              <Table.Td>{formatDate(guardian.createdAt)}</Table.Td>
              {showActions && (
                <Table.Td>
                  <Menu position="bottom-end" withinPortal>
                    <Menu.Target>
                      <ActionIcon variant="subtle">
                        <IconDotsVertical size={16} />
                      </ActionIcon>
                    </Menu.Target>
                    <Menu.Dropdown>
                      <Menu.Item
                        leftSection={<IconEye size={14} />}
                        onClick={() => handleViewGuardian(guardian)}
                      >
                        View Details
                      </Menu.Item>
                      <Menu.Item
                        leftSection={<IconBan size={14} />}
                        color="orange"
                        onClick={() => handleSuspendGuardian(guardian)}
                      >
                        Suspend
                      </Menu.Item>
                      <Menu.Item
                        leftSection={<IconTrash size={14} />}
                        color="red"
                        onClick={() => handleRevokeGuardian(guardian)}
                      >
                        Revoke
                      </Menu.Item>
                    </Menu.Dropdown>
                  </Menu>
                </Table.Td>
              )}
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    );
  };

  if (loading && myGuardians.length === 0) {
    return (
      <Container size="lg" py="xl">
        <Group justify="center" py="xl">
          <Loader />
        </Group>
      </Container>
    );
  }

  return (
    <Container size="lg" py="xl">
      <Group justify="space-between" mb="xl">
        <div>
          <Title order={2}>Guardians</Title>
          <Text c="dimmed" size="sm">
            Manage who can access your protected information
          </Text>
        </div>
        <Button leftSection={<IconPlus size={16} />} onClick={handleAddGuardian}>
          Add Guardian
        </Button>
      </Group>

      {error && (
        <Alert
          icon={<IconAlertCircle size={16} />}
          color="red"
          mb="md"
          onClose={clearError}
          withCloseButton
        >
          {error}
        </Alert>
      )}

      <Tabs defaultValue="my-guardians">
        <Tabs.List mb="md">
          <Tabs.Tab value="my-guardians" leftSection={<IconUsers size={14} />}>
            My Guardians ({myGuardians.length})
          </Tabs.Tab>
          <Tabs.Tab value="guarded-users" leftSection={<IconUserCheck size={14} />}>
            Users I Guard ({guardedUsers.length})
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="my-guardians">
          <Stack>
            <Paper p="md" bg="blue.0" mb="md">
              <Text size="sm" c="blue.9">
                Guardians can access your L1G information (name, date of birth, medical info)
                but never your financial data (SSN, bank accounts). There is a 48-hour delay
                before any guardian can access your data.
              </Text>
            </Paper>

            {renderGuardianTable(myGuardians)}
          </Stack>
        </Tabs.Panel>

        <Tabs.Panel value="guarded-users">
          <Stack>
            <Paper p="md" bg="green.0" mb="md">
              <Text size="sm" c="green.9">
                These are the users who have enrolled you as their guardian.
                When you request access, there will be a 48-hour delay during which
                they can cancel the request.
              </Text>
            </Paper>

            {renderGuardianTable(guardedUsers, false)}
          </Stack>
        </Tabs.Panel>
      </Tabs>
    </Container>
  );
}

export default GuardianDashboard;
